import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useInboxConversations } from "@/features/inbox/api";
import { NAV_GROUPS, type NavItem } from "./nav";

// NAV-AUDIT-01 groups (the day / Sell & Build / Company) live in nav.ts —
// shared with the top-bar breadcrumb so the two can't drift (CRUMB-NAV-01).
const [dayGroup, sellGroup, companyGroup] = NAV_GROUPS;
const primaryNav = dayGroup.items;
const workNav = sellGroup.items;
const secondaryNav = companyGroup.items;

const COLLAPSE_KEY = "pegasus.sidebar_collapsed";
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

// Brand logo knockout. The source PNGs are a dark mark on a WHITE block, which on the
// dark nav reads as a sticker. We knock the white background out and recolor the mark to
// a warm white at runtime (canvas), cached once per session — a clean white logo on the
// dark nav, no box. (We can't ship a pre-baked transparent asset from here, so we bake it
// client-side; same-origin, so the canvas is never tainted.)
const _logoCache: Record<string, string> = {};
function useBrandLogo(src: string): string | null {
  const [url, setUrl] = useState<string | null>(_logoCache[src] ?? null);
  useEffect(() => {
    if (_logoCache[src]) { setUrl(_logoCache[src]); return; }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      const H = 128;
      const W = Math.round((img.naturalWidth * H) / img.naturalHeight) || H;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const x = c.getContext("2d");
      if (!x) return;
      x.drawImage(img, 0, 0, W, H);
      try {
        const d = x.getImageData(0, 0, W, H);
        const p = d.data;
        // NAV-LOGO-01 (Seth 2026-09-03): the collapsed mark (pegasus-mark.png) is
        // ALREADY a white mark on a transparent background. Running the white-
        // knockout on it inverted it — the transparent ground painted white and
        // the pegasus punched out — which is the "white background one" he saw.
        // A source with real transparency is used as-is (tinted to the same
        // warm white); only opaque white-block sources get knocked out.
        let transparent = 0;
        for (let i = 3; i < p.length; i += 4) if (p[i] < 16) transparent += 1;
        const preCut = transparent > (p.length / 4) * 0.05;
        for (let i = 0; i < p.length; i += 4) {
          if (preCut) {
            p[i] = 242; p[i + 1] = 239; p[i + 2] = 232; // keep the alpha the asset has
            continue;
          }
          const lum = p[i] * 0.299 + p[i + 1] * 0.587 + p[i + 2] * 0.114;
          let a = (255 - lum) * 1.75;
          if (a > 255) a = 255;
          p[i] = 242; p[i + 1] = 239; p[i + 2] = 232; p[i + 3] = a;
        }
        x.putImageData(d, 0, 0);
        const out = c.toDataURL("image/png");
        _logoCache[src] = out;
        if (!cancelled) setUrl(out);
      } catch {
        if (!cancelled) setUrl(src); // tainted (shouldn't happen) → raw asset
      }
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return url;
}

/**
 * SHELL-HD (2026-09-01) — the rail every screen is framed by. The language is
 * Linear / Notion: a calm dark rail, 36px rows with 18px icons, the active
 * row lifted on a ring, its icon tinted brand-blue for identity, and the
 * collapse control as an EDGE HANDLE on the rail's border (hover to reveal,
 * always shown when collapsed) instead of a labeled bar at the bottom.
 */
export function Sidebar() {
  const reduceMotion = useReducedMotion();
  // INBOX-DOT (Seth 2026-08-28): unread conversations light the rail — the
  // shared inbox query already live-refetches every 30s.
  const inboxUnread = (useInboxConversations().data ?? []).filter((c) => c.unread && c.conv_status !== "done").length; // done convs don't count — the view hides them (Seth 2026-08-28)
  // SETTINGS-AUDIT-01: hide the Settings link from accounts with no settings
  // capability at all (the page itself also gates — this is just less clutter).
  const canSettings = useCan("settings.manage");
  const canUsers = useCan("users.manage");
  const canAudit = useCan("audit.view");
  const visibleSecondary = secondaryNav.filter(
    (i) => i.to !== "/settings" || canSettings || canUsers || canAudit,
  );
  const [collapsedPref, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsedPref));
    } catch {
      /* ignore */
    }
  }, [collapsedPref]);

  // QUOTE-POLISH-12 (tablet): below `lg` (an iPad held portrait) the full
  // 240px rail eats a third of the screen, so the rail is icons-only there
  // regardless of the saved preference; the preference still rules on desktop.
  const narrow = !useMediaQuery("(min-width: 1024px)");
  const collapsed = collapsedPref || narrow;
  // RAIL-SHORT-01 (Seth 2026-10-18, a short laptop window: "I don't ever want
  // to see the scroll wheel line on the side bar"): under ~820px of height the
  // rail tightens — 32px rows, closer group labels, a shorter logo row, no
  // wordmark — so all fourteen rows fit; and the nav's scrollbar is hidden
  // outright (it still scrolls by wheel/touch if a screen is shorter still).
  const short = useMediaQuery("(max-height: 820px)");

  const logoUrl = useBrandLogo(collapsed ? "/pegasus-mark.png" : "/pegasus-logo.png");

  return (
    <aside
      // Dark navigation (Seth 2026-06-26, Run a Call alignment) — warm near-black.
      // DESKTOP-GLASS-01 (Seth picked "A - warm glass"): the same dark rail,
      // FLOATING — inset 12px all round, rounded, a soft shadow on the cream
      // (index.css [data-glass="rail"] paints it). No overflow-hidden: the
      // collapse handle hangs off the edge.
      data-glass="rail"
      className={cn(
        "group/rail relative z-20 m-3 flex flex-col rounded-[22px] transition-[width] duration-200 ease-out-quart",
        collapsed ? "w-[68px]" : "w-60",
      )}
    >
      {/* Logo + brand row */}
      <div
        className={cn(
          "flex shrink-0 items-center transition-all",
          short ? "h-12" : "h-16",
          collapsed ? "justify-center px-2" : "justify-start px-4",
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Pegasus"
            className={cn(
              "object-contain transition-all",
              collapsed ? "h-9 w-9" : short ? "h-9 max-w-full" : "h-12 max-w-full",
            )}
          />
        ) : (
          // Reserve the height while the knockout bakes (first paint) — no layout shift.
          <div className={collapsed ? "h-9 w-9" : short ? "h-9 w-full" : "h-12 w-full"} aria-hidden />
        )}
      </div>

      {/* Edge handle — collapse / expand. Lives ON the rail's border like
          Notion's, reveals on rail hover; when collapsed it stays visible so
          the way back is obvious. */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cn(
          "absolute -right-3 top-[22px] z-30 flex h-6 w-6 items-center justify-center rounded-full border border-hairline bg-white text-slate-500 shadow-e2 transition-all duration-150 ease-out-quart hover:scale-110 hover:text-slate-900 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          collapsed ? "opacity-100" : "opacity-0 group-hover/rail:opacity-100",
          narrow && "hidden", // the screen decides below lg — no handle that does nothing
        )}
      >
        {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
      </button>

      {/* Nav — three labeled groups: the day, the pipeline, the business. */}
      <nav className={cn("flex-1 overflow-y-auto overflow-x-hidden px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", short ? "py-1.5" : "py-3")}>
        <ul className="space-y-0.5">
          {primaryNav.map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} dense={short} reduceMotion={!!reduceMotion} badge={item.to === "/inbox" ? inboxUnread : undefined} />
          ))}
        </ul>
        <GroupLabel collapsed={collapsed} dense={short}>{sellGroup.label}</GroupLabel>
        <ul className="space-y-0.5">
          {workNav.map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} dense={short} reduceMotion={!!reduceMotion} />
          ))}
        </ul>
        <GroupLabel collapsed={collapsed} dense={short}>{companyGroup.label}</GroupLabel>
        <ul className="space-y-0.5">
          {visibleSecondary.map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} dense={short} reduceMotion={!!reduceMotion} />
          ))}
        </ul>
      </nav>

      {/* Foot — a quiet wordmark line, the way Linear signs its rail (skipped on a short screen). */}
      {!collapsed && !short && (
        <div className="flex h-10 shrink-0 items-center px-4 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/25">
          Pegasus
        </div>
      )}
    </aside>
  );
}

function GroupLabel({ collapsed, dense, children }: { collapsed: boolean; dense?: boolean; children: React.ReactNode }) {
  return collapsed ? (
    <div className={cn("mx-auto h-px w-6 bg-white/10", dense ? "my-2" : "my-3")} />
  ) : (
    <div className={cn("mb-1 px-2.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/35", dense ? "mt-3.5" : "mt-6")}>
      {children}
    </div>
  );
}

function NavItemLink({
  item,
  collapsed,
  dense,
  reduceMotion,
  badge,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Short screen (RAIL-SHORT-01): 32px rows instead of 36. */
  dense?: boolean;
  reduceMotion: boolean;
  badge?: number;
}) {
  return (
    <li>
      <NavLink
        to={item.to}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            "relative flex items-center rounded-xl text-[13.5px] font-semibold transition-colors duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/50",
            collapsed ? (dense ? "mx-auto h-9 w-10 justify-center" : "mx-auto h-10 w-10 justify-center") : (dense ? "h-8 gap-2.5 px-2.5" : "h-9 gap-2.5 px-2.5"),
            isActive
              ? "text-white"
              : "text-white/60 hover:bg-white/[0.06] hover:text-white",
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Active background slides between items (shared layoutId) — the
                same navigation-feel language as the tab underlines. */}
            {isActive && (
              <motion.span
                layoutId="sidebar-nav-active"
                className="absolute inset-0 rounded-xl bg-white/[0.10] ring-1 ring-inset ring-white/[0.08]"
                transition={reduceMotion ? { duration: 0 } : { duration: 0.25, ease: EASE_OUT_EXPO }}
              />
            )}
            <span className="relative z-10 flex-shrink-0">
              <item.icon
                className={cn("h-[18px] w-[18px] transition-colors", isActive ? "text-brand-300" : "")}
                strokeWidth={1.75}
              />
              {collapsed && (badge ?? 0) > 0 && (
                <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#1c1a16]" />
              )}
            </span>
            {!collapsed && <span className="relative z-10 flex-1 truncate">{item.label}</span>}
            {!collapsed && (badge ?? 0) > 0 && (
              <span className="relative z-10 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10.5px] font-bold leading-none text-white tabular-nums">
                {badge! > 99 ? "99+" : badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}
